import { z } from "zod";
import { DomainError } from "./errors.js";
import { ANSWER_MESSAGE_DRAFT_MAX_LENGTH, isAgentProposalMessageDraftWithinBound, isUnicodeScalarTextWithinLength } from "./proposal-message-draft.js";
export const WorkStatusSchema = z.enum([
    "BACKLOG",
    "READY",
    "IN_PROGRESS",
    "INTERNAL_REVIEW",
    "CLIENT_REVIEW",
    "BLOCKED",
    "DONE",
    "ARCHIVED"
]);
export const WorkPrioritySchema = z.enum(["LOW", "NORMAL", "HIGH", "URGENT"]);
export const WorkDepartmentSchema = z.string().trim().min(1).max(160);
export const WorkLocationReferenceSchema = z.string().trim().min(1).max(4_000).refine(isPlainReferenceOrSafeHttpUrl, "Use an HTTP(S) link or a plain location reference.");
const EXPLICIT_URI_SCHEME = /^([a-z][a-z0-9+.-]*):/iu;
function isPlainReferenceOrSafeHttpUrl(value) {
    const scheme = EXPLICIT_URI_SCHEME.exec(value)?.[1]?.toLocaleLowerCase("en");
    if (!scheme)
        return true;
    if (!/^https?:\/\//iu.test(value) || (scheme !== "http" && scheme !== "https"))
        return false;
    try {
        const parsed = new URL(value);
        return parsed.protocol === "http:" || parsed.protocol === "https:";
    }
    catch {
        return false;
    }
}
export const WorkKindSchema = z.enum(["TASK", "ENGAGEMENT"]);
/**
 * WHY [BOL-WORK-SCHEDULE-SEMANTICS-001]: a deadline/checkpoint is work timing;
 * a shoot, meeting, call, or visit is a real scheduled engagement. Reusing
 * `dueAt` for both made Calendar and Ops Agent disagree. A Task Timeline range
 * is also distinct from its deadline: it is nullable, requires both endpoints,
 * and may span one day. The model may propose the semantic kind, but this
 * deterministic boundary keeps Task ranges out of Calendar and engagement
 * timing out of Timeline. Falsifier: an ordinary deadline becomes a fake range,
 * or one Work record carries both Task and engagement timing.
 */
export function validateWorkSchedule(fields) {
    const kind = fields.workKind ?? "TASK";
    const taskStart = fields.taskStartAt ?? null;
    const taskEnd = fields.taskEndAt ?? null;
    const start = fields.scheduledStartAt ?? null;
    const end = fields.scheduledEndAt ?? null;
    const allDay = fields.scheduledAllDay ?? false;
    if (kind === "TASK") {
        if (start || end || allDay)
            return "A task cannot carry scheduled-engagement timing.";
        if (Boolean(taskStart) !== Boolean(taskEnd))
            return "A task Timeline range needs both a start and an end.";
        if (taskStart && taskEnd && Date.parse(taskEnd) < Date.parse(taskStart)) {
            return "A task Timeline range cannot end before it starts.";
        }
        return null;
    }
    if (taskStart || taskEnd)
        return "A scheduled engagement cannot carry Task Timeline timing.";
    if (!start)
        return "A scheduled engagement needs a start time.";
    if (end && Date.parse(end) <= Date.parse(start))
        return "A scheduled engagement must end after it starts.";
    return null;
}
function addWorkScheduleIssue(fields, context) {
    const issue = validateWorkSchedule(fields);
    if (issue)
        context.addIssue({ code: "custom", path: ["scheduledStartAt"], message: issue });
}
export const ApprovalStatusSchema = z.enum([
    "NOT_REQUIRED",
    "NOT_SUBMITTED",
    "INTERNAL_PENDING",
    "CLIENT_PENDING",
    "APPROVED",
    "CHANGES_REQUESTED"
]);
export const PublicationStatusSchema = z.enum([
    "NOT_APPLICABLE",
    "NOT_SCHEDULED",
    "SCHEDULED",
    "PUBLISHED"
]);
export const WorkItemSchema = z.object({
    id: z.uuid(),
    workspaceId: z.uuid(),
    clientId: z.uuid().nullable(),
    projectId: z.uuid().nullable(),
    title: z.string().min(1).max(240),
    description: z.string().max(10_000),
    status: WorkStatusSchema,
    priority: WorkPrioritySchema,
    approvalStatus: ApprovalStatusSchema,
    publicationStatus: PublicationStatusSchema,
    assigneeId: z.uuid().nullable(),
    assignedByMemberId: z.uuid().nullable().default(null),
    assignedAt: z.iso.datetime().nullable().default(null),
    department: WorkDepartmentSchema.nullable().default(null),
    dueAt: z.iso.datetime().nullable(),
    taskStartAt: z.iso.datetime().nullable().default(null),
    taskEndAt: z.iso.datetime().nullable().default(null),
    filesReference: WorkLocationReferenceSchema.nullable().default(null),
    reviewFeedbackReference: WorkLocationReferenceSchema.nullable().default(null),
    nextCheckpointAt: z.iso.datetime().nullable(),
    workKind: WorkKindSchema.optional(),
    scheduledStartAt: z.iso.datetime().nullable().optional(),
    scheduledEndAt: z.iso.datetime().nullable().optional(),
    scheduledAllDay: z.boolean().optional(),
    blockedReason: z.string().max(2_000).nullable(),
    manualLock: z.boolean(),
    sortOrder: z.number().finite(),
    version: z.number().int().positive(),
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime()
}).superRefine(addWorkScheduleIssue);
export const CreateWorkItemSchema = z.object({
    title: z.string().trim().min(1).max(240),
    description: z.string().trim().max(10_000).default(""),
    clientId: z.uuid().nullable().default(null),
    projectId: z.uuid().nullable().default(null),
    status: WorkStatusSchema.default("BACKLOG"),
    priority: WorkPrioritySchema.default("NORMAL"),
    assigneeId: z.uuid().nullable().default(null),
    assignedByMemberId: z.uuid().nullable().default(null),
    assignedAt: z.iso.datetime().nullable().default(null),
    department: WorkDepartmentSchema.nullable().default(null),
    dueAt: z.iso.datetime().nullable().default(null),
    taskStartAt: z.iso.datetime().nullable().default(null),
    taskEndAt: z.iso.datetime().nullable().default(null),
    filesReference: WorkLocationReferenceSchema.nullable().default(null),
    reviewFeedbackReference: WorkLocationReferenceSchema.nullable().default(null),
    nextCheckpointAt: z.iso.datetime().nullable().optional(),
    workKind: WorkKindSchema.optional(),
    scheduledStartAt: z.iso.datetime().nullable().optional(),
    scheduledEndAt: z.iso.datetime().nullable().optional(),
    scheduledAllDay: z.boolean().optional()
}).superRefine(addWorkScheduleIssue);
export const UpdateWorkItemSchema = z.object({
    expectedVersion: z.number().int().positive(),
    title: z.string().trim().min(1).max(240).optional(),
    description: z.string().trim().max(10_000).optional(),
    clientId: z.uuid().nullable().optional(),
    projectId: z.uuid().nullable().optional(),
    status: WorkStatusSchema.optional(),
    priority: WorkPrioritySchema.optional(),
    assigneeId: z.uuid().nullable().optional(),
    assignedByMemberId: z.uuid().nullable().optional(),
    assignedAt: z.iso.datetime().nullable().optional(),
    department: WorkDepartmentSchema.nullable().optional(),
    dueAt: z.iso.datetime().nullable().optional(),
    taskStartAt: z.iso.datetime().nullable().optional(),
    taskEndAt: z.iso.datetime().nullable().optional(),
    filesReference: WorkLocationReferenceSchema.nullable().optional(),
    reviewFeedbackReference: WorkLocationReferenceSchema.nullable().optional(),
    nextCheckpointAt: z.iso.datetime().nullable().optional(),
    workKind: WorkKindSchema.optional(),
    scheduledStartAt: z.iso.datetime().nullable().optional(),
    scheduledEndAt: z.iso.datetime().nullable().optional(),
    scheduledAllDay: z.boolean().optional(),
    blockedReason: z.string().trim().max(2_000).nullable().optional(),
    manualLock: z.boolean().optional(),
    sortOrder: z.number().finite().optional()
});
export const WORK_STATUS_LABELS = {
    BACKLOG: "Backlog",
    READY: "Ready",
    IN_PROGRESS: "In progress",
    INTERNAL_REVIEW: "Internal review",
    CLIENT_REVIEW: "Client review",
    BLOCKED: "Blocked",
    DONE: "Done",
    ARCHIVED: "Archived"
};
const ALLOWED_TRANSITIONS = {
    BACKLOG: new Set(["READY", "IN_PROGRESS", "ARCHIVED"]),
    // WHY [BOL-OWNER-READY-COMPLETE-001]: work can finish in WhatsApp before the
    // board is moved to In progress. An explicit human-approved correction may
    // record that truth directly; automated completion claims still use
    // statusForDoneClaim below and cannot skip review. Falsifier: “finished” on a
    // Ready task cannot be approved, or an unapproved inbound claim jumps Done.
    READY: new Set(["BACKLOG", "IN_PROGRESS", "BLOCKED", "DONE", "ARCHIVED"]),
    IN_PROGRESS: new Set(["READY", "INTERNAL_REVIEW", "CLIENT_REVIEW", "BLOCKED", "DONE", "ARCHIVED"]),
    INTERNAL_REVIEW: new Set(["IN_PROGRESS", "CLIENT_REVIEW", "BLOCKED", "DONE", "ARCHIVED"]),
    CLIENT_REVIEW: new Set(["IN_PROGRESS", "INTERNAL_REVIEW", "BLOCKED", "DONE", "ARCHIVED"]),
    BLOCKED: new Set(["READY", "IN_PROGRESS", "INTERNAL_REVIEW", "CLIENT_REVIEW", "DONE", "ARCHIVED"]),
    DONE: new Set(["IN_PROGRESS", "ARCHIVED"]),
    ARCHIVED: new Set(["BACKLOG", "READY", "IN_PROGRESS", "DONE"])
};
export function assertAllowedWorkTransition(from, to) {
    if (from === to)
        return;
    if (!ALLOWED_TRANSITIONS[from].has(to)) {
        throw new DomainError("INVALID_WORK_TRANSITION", `Work cannot move directly from ${WORK_STATUS_LABELS[from]} to ${WORK_STATUS_LABELS[to]}.`, { from, to });
    }
}
/** A completion claim advances production, but never invents approval or
 * publication. Both WhatsApp update paths share this owner so they cannot
 * develop conflicting definitions of "done". */
export function statusForDoneClaim(item) {
    if (item.approvalStatus === "APPROVED" && ["PUBLISHED", "NOT_APPLICABLE"].includes(item.publicationStatus))
        return "DONE";
    if (["IN_PROGRESS", "BLOCKED"].includes(item.status))
        return "INTERNAL_REVIEW";
    if (["BACKLOG", "READY"].includes(item.status))
        return "IN_PROGRESS";
    return item.status;
}
export const BoardMemberSchema = z.object({
    id: z.uuid(),
    displayName: z.string(),
    role: z.enum(["OWNER", "MANAGER", "CONTRIBUTOR", "VIEWER"]),
    avatarSeed: z.string(),
    // The board exposes only readiness, never the private channel identifier.
    whatsAppLinked: z.boolean().optional(),
    whatsAppIdentityState: z.enum(["VERIFIED", "SYNCING", "UNRESOLVED", "AMBIGUOUS"]).optional()
});
export const BoardClientSchema = z.object({
    id: z.uuid(),
    name: z.string(),
    color: z.string()
});
export const BoardProjectSchema = z.object({
    id: z.uuid(),
    clientId: z.uuid(),
    name: z.string()
});
export const WhatsAppOutboundDeliveryStateSchema = z.enum(["SENT", "DELIVERED", "READ", "PLAYED"]);
export const ScheduledSendFailureReasonSchema = z.enum([
    "EXPIRED",
    "WORK_CHANGED",
    "CONVERSATION_CHANGED",
    "ROUTE_CHANGED",
    "POLICY_CHANGED",
    "TARGET_CHANGED",
    "TIMEZONE_CHANGED",
    "TRANSPORT_CHANGED",
    "OTHER"
]);
export const WorkAgentStateSchema = z.object({
    workItemId: z.uuid(),
    proposalId: z.uuid(),
    proposalType: z.enum([
        "ANSWER_MESSAGE",
        "FOLLOWUP_MESSAGE",
        "CLARIFICATION_MESSAGE",
        "ESCALATION_MESSAGE"
    ]),
    stage: z.enum([
        "SCHEDULED",
        "SCHEDULE_NEEDS_REVIEW",
        "FINAL_CHECKS",
        "WAITING_FOR_REPLY",
        "MESSAGE_SENT",
        "DELIVERY_NEEDS_CHECK"
    ]),
    targetMemberId: z.uuid().nullable(),
    destinationLabel: z.string().min(1).max(200),
    message: z.string().min(1).max(ANSWER_MESSAGE_DRAFT_MAX_LENGTH * 2).refine((value) => isUnicodeScalarTextWithinLength(value, ANSWER_MESSAGE_DRAFT_MAX_LENGTH), { message: "The projected message must contain at most 2,000 valid Unicode characters." }),
    sentAt: z.iso.datetime().nullable(),
    nextCheckAt: z.iso.datetime().nullable(),
    deliveryState: WhatsAppOutboundDeliveryStateSchema.nullable(),
    deliveredAt: z.iso.datetime().nullable(),
    readAt: z.iso.datetime().nullable(),
    playedAt: z.iso.datetime().nullable(),
    scheduledAt: z.iso.datetime().nullable().default(null),
    scheduleTimeZone: z.string().min(1).max(80).nullable().default(null),
    scheduleUtcOffset: z.string().regex(/^[+-](?:0\d|1[0-4]):[0-5]\d$/u).nullable().default(null),
    scheduleFailureReason: ScheduledSendFailureReasonSchema.nullable().default(null),
    updatedAt: z.iso.datetime()
}).superRefine((state, context) => {
    if (!isAgentProposalMessageDraftWithinBound(state.proposalType, state.message)) {
        context.addIssue({
            code: "custom",
            path: ["message"],
            message: "The projected message exceeds its proposal-type bound."
        });
    }
    const scheduled = state.stage === "SCHEDULED" || state.stage === "SCHEDULE_NEEDS_REVIEW";
    if (scheduled && (!state.scheduledAt || !state.scheduleTimeZone || !state.scheduleUtcOffset)) {
        context.addIssue({ code: "custom", message: "A scheduled Work state needs its exact approved time." });
    }
    if (state.stage === "SCHEDULE_NEEDS_REVIEW" && state.scheduleFailureReason === null) {
        context.addIssue({ code: "custom", message: "A failed schedule needs one safe review reason." });
    }
    if (state.stage !== "SCHEDULE_NEEDS_REVIEW" && state.scheduleFailureReason !== null) {
        context.addIssue({ code: "custom", message: "Only a failed schedule may expose a review reason." });
    }
});
export const BoardSnapshotSchema = z.object({
    workspace: z.object({ id: z.uuid(), name: z.string(), slug: z.string(), timezone: z.string() }),
    viewer: BoardMemberSchema,
    members: z.array(BoardMemberSchema),
    clients: z.array(BoardClientSchema),
    projects: z.array(BoardProjectSchema),
    workItems: z.array(WorkItemSchema),
    workAgentStates: z.array(WorkAgentStateSchema).default([]),
    dailyWorkSummary: z.object({
        dayKey: z.string().regex(/^\d{4}-\d{2}-\d{2}$/u),
        completedWorkItemIds: z.array(z.uuid())
    }).optional(),
    generatedAt: z.iso.datetime()
});
/**
 * WHY [BOL-WORK-ATTENTION-001]: the approval card must explain the literal
 * business fact that made a follow-up necessary, not expose an internal rule
 * token such as `work_is_overdue`. Both the worker and every channel-neutral
 * approval surface use this one formatter so WhatsApp today and Instagram UGC
 * outreach later cannot develop different explanations for the same task.
 * This is display evidence only; it grants no route, recipient, or send
 * authority. Falsifier: Review says only "work is overdue" or a new channel
 * reimplements its own why-now business logic.
 */
export function describeWorkAttention(item, now, timezone) {
    const title = item.title.trim();
    const status = WORK_STATUS_LABELS[item.status].toLocaleLowerCase("en");
    if (item.status === "BLOCKED") {
        const blocker = item.blockedReason?.trim();
        return blocker ? `${title} is blocked: ${blocker}` : `${title} is blocked and needs an update.`;
    }
    if (item.dueAt) {
        const dueAt = new Date(item.dueAt);
        if (Number.isFinite(dueAt.getTime())) {
            const due = new Intl.DateTimeFormat("en", {
                timeZone: timezone,
                month: "short",
                day: "numeric"
            }).format(dueAt);
            return `${title} ${dueAt.getTime() < now.getTime() ? "was due" : "is due"} ${due} and is still ${status}.`;
        }
    }
    if (item.nextCheckpointAt)
        return `${title} needs a status update and is still ${status}.`;
    return `${title} needs the next confirmed update.`;
}
//# sourceMappingURL=work-items.js.map